import { Box, Button, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Dialog } from '@/components/common/Dialog';
import { useConditionRadarSeries } from '@/components/home/cards/HomeScoreCard';
import { PropertyLocationMap } from '@/components/home/onboarding/PropertyLocationMap';
import { StepperHeader, StepperHeaderStep } from '@/components/home/onboarding/StepperHeader';
import { DetailsStep } from '@/components/home/onboarding/steps/DetailsStep';
import { ProcessingStep } from '@/components/home/onboarding/steps/ProcessingStep';
import { PropertyStep } from '@/components/home/onboarding/steps/PropertyStep';
import { UnveilStep } from '@/components/home/onboarding/steps/UnveilStep';
import { UploadStep } from '@/components/home/onboarding/steps/UploadStep';
import { useScoreArrivalFallback } from '@/components/home/onboarding/useScoreArrivalFallback';
import { useAuthContext } from '@/context/AuthProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useGetIssuesForProperty } from '@/hooks/issues/useGetIssuesForProperty';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useUserRole } from '@/hooks/useUserRole';
import { isUserProfileIncomplete } from '@/utils/user';

type OnboardingStep = 'property' | 'details' | 'upload' | 'processing' | 'unveil';
type StepperStep = 'property' | 'details' | 'upload';

const STEPPER_STEPS: StepperHeaderStep[] = [
  { key: 'property', label: 'Property address' },
  { key: 'details', label: 'Personal details' },
  { key: 'upload', label: 'Upload report' },
];
const STEPPER_INDEX: Record<StepperStep, number> = { property: 0, details: 1, upload: 2 };

const isStepperStep = (step: OnboardingStep): step is StepperStep =>
  step === 'property' || step === 'details' || step === 'upload';

export interface SelfServeOnboardingModalProps {
  isMobile: boolean;
}

export const SelfServeOnboardingModal = ({ isMobile }: SelfServeOnboardingModalProps) => {
  const { user, logOut } = useAuthContext();
  const { hasOwnedProperties, isInitialFetchingProperties, selectedProperty } =
    usePropertiesContext();
  const { isAdvisor, canAccessAdmin } = useUserRole();
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const propertyId = selectedProperty?.property.id;
  const {
    conditionScore,
    isProcessing: isEqProcessing,
    isLoading: isLoadingEqResults,
  } = useHauserEQResults(propertyId);
  const radarSeries = useConditionRadarSeries(propertyId);
  const { issues } = useGetIssuesForProperty(propertyId ?? '');
  const { stepProcessing, resetProcessing } = useUploaderContext();
  // UploaderContext advances stepProcessing 1→4 while uploading/analyzing,
  // then to 5 on success (its own isProcessingActive treats 5 as "done", not
  // "active" — see UploaderProvider.tsx). Mirror that here: `stepProcessing
  // > 0` would misread the post-success value 5 as still-processing and
  // deadlock this modal on the Processing step forever, since nothing else
  // in this flow resets it back to 0 (SidebarProcessingIndicator normally
  // does, but it isn't mounted on mobile, and this modal shouldn't depend on
  // an unrelated component's cleanup timer anyway).
  const isUploadStepProcessing = stepProcessing >= 1 && stepProcessing < 5;

  // If the pipeline's final WebSocket message never arrives, finish on the
  // score itself. Resetting ends processing, and the effect below then plays
  // the unveil exactly as it would for the WebSocket path.
  useScoreArrivalFallback({
    propertyId,
    isUploadProcessing: isUploadStepProcessing,
    conditionScore,
    isEqProcessing,
    onScoreArrived: resetProcessing,
  });

  const [showUnveil, setShowUnveil] = useState(false);
  // Set only by clicking back to an already-completed step, to review/edit
  // it — cleared once that review is done, snapping back to the real step.
  const [reviewStep, setReviewStep] = useState<StepperStep | null>(null);
  const wasProcessingRef = useRef(false);
  // Mobile-only: a shared slot, pinned just above the "Log out" footer link,
  // that each step's own Continue button portals into instead of rendering
  // inline — keeps every step's button logic untouched, just relocates
  // where it visually renders. State (not a plain ref) because the portal
  // target has to exist in the DOM before children can render into it.
  const [ctaSlot, setCtaSlot] = useState<HTMLDivElement | null>(null);

  const isSelfServeOwner = !isAdvisor && !canAccessAdmin && !isLoadingGroups && groups.length === 0;

  // Fires exactly once, on the transition INTO success — not on every render
  // where conditionScore happens to be set — so a cold return visit (score
  // already resolved before this component ever mounted) skips straight to
  // the normal dashboard instead of replaying the reveal.
  useEffect(() => {
    const processingNow = isUploadStepProcessing || isEqProcessing;
    if (wasProcessingRef.current && !processingNow && conditionScore != null) {
      setShowUnveil(true);
      // Own our cleanup instead of relying on SidebarProcessingIndicator's
      // 4s auto-reset timer, which isn't mounted at all on mobile.
      if (stepProcessing !== 0) resetProcessing();
    }
    wasProcessingRef.current = processingNow;
  }, [stepProcessing, isUploadStepProcessing, isEqProcessing, conditionScore, resetProcessing]);

  const baseStep = (): OnboardingStep | null => {
    if (!hasOwnedProperties) return 'property';
    if (user && isUserProfileIncomplete(user)) return 'details';
    if (isUploadStepProcessing || isEqProcessing) return 'processing';
    if (conditionScore == null) return 'upload';
    return null;
  };

  const derivedStep = showUnveil ? 'unveil' : baseStep();
  // How far the user has truly progressed, for the stepper's checkmarks —
  // independent of which step is actually on screen right now.
  const completedIndex = derivedStep && isStepperStep(derivedStep) ? STEPPER_INDEX[derivedStep] : 3;

  // A review is only valid while it's still genuinely in the past — if
  // something upstream ever moved completedIndex backward (e.g. the claimed
  // property was deleted out from under an open review), drop it rather
  // than show a "completed" step that no longer is one.
  const isValidReview = reviewStep != null && STEPPER_INDEX[reviewStep] < completedIndex;
  const step: OnboardingStep | null = isValidReview ? reviewStep : derivedStep;

  // Self-heal rather than leave a stale reviewStep sitting in state once
  // it's no longer valid — nothing else needs to re-derive around it.
  useEffect(() => {
    if (reviewStep != null && !isValidReview) setReviewStep(null);
  }, [reviewStep, isValidReview]);

  // Guards the *initial* open only — without it, the modal would flash open
  // on first load while properties/groups/EQ results are still being
  // fetched, then immediately snap shut once the real (often-null) data
  // arrives. It must NOT gate re-closing once already open: claiming a
  // property kicks off that property's first-ever EQ-results fetch, which
  // sets isLoadingEqResults true again for a beat — without the latch below,
  // that alone would flip isOpen false mid-flow and drop the user onto the
  // bare dashboard for a frame before the modal reappears.
  //
  // The `propertyId != null` clause closes a real gap: selectedProperty
  // resolves in its own effect a render after `properties` loads, so
  // there's a window where hasOwnedProperties is already true but
  // propertyId (and therefore the EQ-results query, which is disabled
  // without one) hasn't caught up yet. A disabled query reports
  // isLoadingEqResults as false — it's not "loaded", it's just not
  // running — so without this clause dataReady goes true on a false
  // signal, conditionScore reads as null (no data, not genuinely null),
  // and baseStep briefly derives 'upload' before the real fetch resolves
  // and the modal snaps shut again. Reproduced by anything that forces a
  // full remount with a cold cache (e.g. the tab being discarded after
  // the OS screensaver/sleep, then reloaded on return).
  const dataReady =
    !isInitialFetchingProperties &&
    !isLoadingGroups &&
    (!hasOwnedProperties || propertyId != null) &&
    !isLoadingEqResults;
  const hasOpenedRef = useRef(false);
  const isOpen = isSelfServeOwner && step != null && (dataReady || hasOpenedRef.current);
  useEffect(() => {
    hasOpenedRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    // On mobile the dialog is pinned into the small strip below the fixed
    // nav bar, not centered over the content — a blurred dashboard sliver
    // around it doesn't read as a meaningful preview, just visual noise.
    const mainContent = document.getElementById('dashboard-main-content');
    if (!mainContent || !isOpen || isMobile) return;

    const previous = {
      filter: mainContent.style.filter,
      clipPath: mainContent.style.clipPath,
      pointerEvents: mainContent.style.pointerEvents,
      transition: mainContent.style.transition,
    };
    mainContent.style.transition = 'filter 400ms ease, clip-path 400ms ease';
    mainContent.style.filter = 'blur(3px)';
    mainContent.style.clipPath = 'inset(-24px)';
    mainContent.style.pointerEvents = 'none';

    return () => {
      mainContent.style.filter = previous.filter;
      mainContent.style.clipPath = previous.clipPath;
      mainContent.style.pointerEvents = previous.pointerEvents;
      mainContent.style.transition = previous.transition;
    };
  }, [isOpen, isMobile]);

  if (!isOpen || !step) return null;

  const handleDismiss = () => setShowUnveil(false);
  const handleReturnToCurrent = () => setReviewStep(null);
  // The stepper stays mounted at a fixed position for every step, including
  // the un-numbered Processing/Unveil ones, so the card never reflows —
  // Processing freezes on "Upload Report" as active, Unveil marks all three done.
  const stepperActiveIndex = isStepperStep(step)
    ? STEPPER_INDEX[step]
    : step === 'processing'
      ? STEPPER_INDEX.upload
      : STEPPER_STEPS.length;
  // Details (an 8-field, two-column form) and Property (its own map + form
  // split, whether live or the read-only review) both use the full card
  // width. Everything else stays a comfortable half-width column, centered
  // with padding on either side. On mobile there's no spare width to
  // dedicate to centering, so everything just takes the full card.
  const contentWidth = isMobile || step === 'details' || step === 'property' ? '100%' : '50%';

  return (
    <Dialog
      open
      disableClose={step !== 'unveil'}
      onClose={handleDismiss}
      width={isMobile ? undefined : 980}
      minHeight={isMobile ? undefined : 600}
      containerProps={{ justifyContent: 'flex-start', ...(isMobile && { p: '20px' }) }}
      slotProps={{
        // The blur effect on #dashboard-main-content already de-emphasizes
        // the background — MUI's default backdrop scrim on top of that
        // stacks a second, unintended darkening layer the mockup never had.
        backdrop: { sx: { backgroundColor: 'transparent' } },
        // Everything below is mobile-only — desktop paper is untouched,
        // still governed entirely by the minHeight prop above.
        ...(isMobile && {
          paper: {
            sx: {
              // A genuinely fixed height so it can't grow/shrink per step,
              // and can't leave a gap below it showing the (unblurred, see
              // below) dashboard through.
              height: 'calc(100dvh - 94px)',
              overflowY: 'auto',
              // Hide the scrollbar itself (same treatment DashboardLayout
              // uses) — a visible scrollbar track reserves space only on
              // the right, which reads as lopsided padding.
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              position: 'fixed',
              // Explicit width (not left+right auto-resolve) — an
              // over-constrained left+right+width mix leaves the resolved
              // width to the browser, and Dialog's own base sx already
              // carries a `width` key (undefined on mobile, but still
              // present), which risks winning the tie unpredictably.
              // Setting every box-model property explicitly here removes
              // every other possible source of left/right asymmetry.
              width: 'calc(100vw - 20px)',
              boxSizing: 'border-box',
              // Matches #dashboard-main-content's own mobile paddingTop
              // (84px) and the fixed nav bar's own left/right inset (10px)
              // exactly, so the card sits right where dashboard content
              // normally would — the nav bar's z-index is well below the
              // dialog's, so without this the dialog would cover it.
              top: '84px',
              left: '10px',
              right: 'auto',
              margin: 0,
              padding: 0,
              transform: 'none',
            },
          },
        }),
      }}
      aria-label="Set up your Home HQ"
      footer={
        // The modal is otherwise non-dismissible until Unveil — without this,
        // there's no way to step away and finish later, since it also sits
        // above the sidebar's own logout affordance.
        <Typography
          onClick={() => logOut()}
          sx={{
            fontSize: 12,
            color: 'var(--tx-muted)',
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': { color: 'var(--tx-text)' },
          }}>
          {step === 'unveil' ? 'Log out' : 'Log out and finish this later'}
        </Typography>
      }>
      <Box sx={{ mb: isMobile ? '16px' : '24px', width: '100%' }}>
        <StepperHeader
          steps={STEPPER_STEPS}
          activeIndex={stepperActiveIndex}
          completedIndex={completedIndex}
          onStepClick={(clicked) => setReviewStep(clicked.key as StepperStep)}
          compact={isMobile}
        />
      </Box>
      <Box
        sx={{
          width: contentWidth,
          maxWidth: '100%',
          mx: 'auto',
          height: isMobile ? 'auto' : 480,
          flex: isMobile ? 1 : 'none',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}>
        {step === 'property' &&
          (isValidReview && reviewStep === 'property' ? (
            <PropertyReview
              onContinue={handleReturnToCurrent}
              isMobile={isMobile}
              ctaSlot={isMobile ? ctaSlot : null}
            />
          ) : (
            <PropertyStep isMobile={isMobile} ctaSlot={isMobile ? ctaSlot : null} />
          ))}
        {step === 'details' && (
          <DetailsStep
            onSaved={handleReturnToCurrent}
            ctaSlot={isMobile ? ctaSlot : null}
            isMobile={isMobile}
          />
        )}
        {step === 'upload' && <UploadStep />}
        {step === 'processing' && <ProcessingStep />}
        {step === 'unveil' && conditionScore != null && (
          <UnveilStep
            address={selectedProperty?.property.address}
            series={radarSeries}
            issues={issues}
            onDismiss={handleDismiss}
            ctaSlot={isMobile ? ctaSlot : null}
          />
        )}
      </Box>
      {/* Mobile-only shared Continue slot, pinned between the content and
          the "Log out" footer link — see ctaSlot above. */}
      {isMobile && <Box ref={setCtaSlot} sx={{ mt: '16px', width: '100%' }} />}
    </Dialog>
  );
};

// Property claiming is a one-shot action — once claimed there's no re-claim
// flow, so clicking back to "Property" reviews the address already on file
// rather than reopening the live address search. Mirrors PropertyStep's own
// map / form split (and its mobile stacking) so the two screens read as the
// same step.
const PropertyReview = ({
  onContinue,
  isMobile,
  ctaSlot,
}: {
  onContinue: () => void;
  isMobile?: boolean;
  ctaSlot?: HTMLDivElement | null;
}) => {
  const { selectedProperty } = usePropertiesContext();
  const property = selectedProperty?.property;

  const continueButton = (
    <Button
      fullWidth
      onClick={onContinue}
      sx={{
        height: 40,
        borderRadius: '10px',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        '&:hover': { backgroundColor: '#D6D3CC' },
      }}>
      Continue
    </Button>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '16px' : '24px',
        height: '100%',
      }}>
      <Box
        sx={{
          order: isMobile ? 2 : 0,
          display: 'flex',
          flex: 1,
          minHeight: isMobile ? 160 : undefined,
        }}>
        <PropertyLocationMap latitude={property?.latitude} longitude={property?.longitude} />
      </Box>

      <Stack sx={{ flex: isMobile ? 'none' : 1, order: isMobile ? 1 : 0, justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 600, color: 'var(--tx-text)', mb: '6px' }}>
            Your home
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#918D87' }}>
            This is the property your Home HQ is set up for.
          </Typography>
        </Box>
        {!ctaSlot && continueButton}
      </Stack>
      {ctaSlot && createPortal(continueButton, ctaSlot)}
    </Box>
  );
};
